import { useState } from "react";
import { dateLocale } from "../lib/languages";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Sparkles, Loader2, RefreshCw, History, Lightbulb } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

const AISuggestions = ({ user }) => {
  const { t, locale } = useI18n();
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [context, setContext] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ai/suggestions`, { context: context || null }, { withCredentials: true });
      setSuggestions(response.data);
      setContext("");
      toast.success(t("ai.generated"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("ai.errorGenerating"));
    } finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/ai/suggestions/history`, { withCredentials: true });
      setHistory(response.data);
      setShowHistory(true);
    } catch (error) { console.error("Error fetching history:", error); }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(dateLocale(locale), {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    }).format(date);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            {t("ai.title")}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchHistory} className="text-muted-foreground" data-testid="ai-history-btn">
            <History className="w-4 h-4 mr-1" /> {t("ai.history")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!suggestions && !showHistory && (
          <>
            <p className="text-sm text-muted-foreground">{t("ai.description")}</p>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("ai.contextLabel")}</label>
              <Textarea placeholder={t("ai.contextPlaceholder")} value={context} onChange={(e) => setContext(e.target.value)} rows={3} data-testid="ai-context-input" />
            </div>
            <Button onClick={fetchSuggestions} disabled={loading} className="w-full bg-primary hover:bg-primary/90" data-testid="generate-suggestions-btn">
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("ai.analyzing")}</>) : (<><Sparkles className="w-4 h-4 mr-2" />{t("ai.getSuggestions")}</>)}
            </Button>
          </>
        )}

        {suggestions && !showHistory && (
          <div className="space-y-4">
            {suggestions.mood_summary?.average && (
              <div className="p-3 bg-secondary/50 rounded-lg flex items-center gap-3">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                <div className="text-sm">
                  <span className="text-muted-foreground">{t("ai.basedOn")} </span>
                  <span className="font-medium">{suggestions.mood_summary.entries_analyzed} {t("ai.entriesAnalyzed")}</span>
                  <span className="text-muted-foreground"> - {t("ai.averageMood")}: </span>
                  <span className="font-medium">{suggestions.mood_summary.average}/10</span>
                </div>
              </div>
            )}
            <div className="prose prose-sm max-w-none">
              <div className="whitespace-pre-wrap text-foreground leading-relaxed">{suggestions.suggestions}</div>
            </div>
            <Button variant="outline" onClick={() => setSuggestions(null)} className="w-full" data-testid="new-suggestion-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> {t("ai.newSuggestions")}
            </Button>
          </div>
        )}

        {showHistory && (
          <div className="space-y-4">
            <Button variant="ghost" onClick={() => setShowHistory(false)} className="text-sm">
              &larr; {t("common.cancel")}
            </Button>
            {history.length > 0 ? (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {history.map((item, idx) => (
                  <div key={idx} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">{formatDate(item.created_at)}</span>
                      {item.mood_context?.avg_mood && (
                        <span className="text-xs bg-secondary px-2 py-1 rounded-full">{t("ai.avg")}: {item.mood_context.avg_mood.toFixed(1)}/10</span>
                      )}
                    </div>
                    <p className="text-sm line-clamp-4">{item.suggestions}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">{t("ai.noHistory")}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AISuggestions;
